import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

interface UseApiOperationOptions {
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export function useApiOperation<T = any>(
  operation: (...args: any[]) => Promise<T>,
  options: UseApiOperationOptions = {}
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const execute = useCallback(async (...args: any[]): Promise<T | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await operation(...args);
      
      if (options.successMessage) {
        enqueueSnackbar(options.successMessage, { variant: 'success' });
      }
      
      if (options.onSuccess) {
        options.onSuccess(result);
      }
      
      return result;
    } catch (err: any) {
      const errorMessage = options.errorMessage || 
        err?.response?.data?.detail || 
        err?.message || 
        'An error occurred';
      
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      
      if (options.onError) {
        options.onError(err);
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [operation, options, enqueueSnackbar]);

  return {
    execute,
    loading,
    error,
    clearError: () => setError(null)
  };
}